import type { Maybe } from '@adornis/base/utilTypes.js';
import { Int } from '@adornis/baseql/baseqlTypes.js';
import { Entity, Field } from '@adornis/baseql/decorators.js';
import { registerSubscription } from '@adornis/baseql/metadata/register.js';
import { getCollectionHandle, getRawCollection } from '@adornis/baseql/server/collections.js';
import { publishFilter } from '@adornis/baseql/server/context.js';
import type { BaseQLSelectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { catchError, from, of, switchMap } from 'rxjs';
import { Partner } from '../../master-data-management/partner/entities/Partner.js';
import { WorkshopAttendance } from '../entities/WorkshopAttendance.js';
import { WorkshopAttendanceStatus } from '../entities/WorkshopAttendanceStatus.js';
import { WorkshopFilter } from '../entities/WorkshopFilter.js';
import { WorkshopTrainingAttendance } from '../entities/WorkshopTrainingAttendance.js';

@Entity()
export class WorkshopAttendanceSearchResult extends WorkshopAttendance {
  public static override _class = 'WorkshopAttendanceSearchResult';

  @Field(() => WorkshopTrainingAttendance)
  public training: Maybe<WorkshopTrainingAttendance>;

  @Field(() => Int)
  public daysSinceTraining: Maybe<number>;
}

export const subscribeWorkshopAttendance = registerSubscription({
  type: () => [WorkshopAttendanceSearchResult],
  operationName: 'subscribeWorkshopAttendance',
  resolve: (skip: number, limit: number, filter: WorkshopFilter) => {
    return (gqlFields: BaseQLSelectionSet<WorkshopAttendanceSearchResult>) => {
      const selector = {
        _class: WorkshopAttendance._class,
        ...filter.getMongoFilter(),
      };
      const handle = getCollectionHandle<WorkshopAttendance>(WorkshopAttendance._collectionName);
      return from(publishFilter(WorkshopAttendance._class)).pipe(
        switchMap(publishFilter => {
          return handle.watchQuery(publishFilter ? { $and: [publishFilter, selector] } : selector, {
            skip,
            limit,
          });
        }),
        switchMap(async attendances => {
          const aggregationFilter = filter.getMongoAggergationFilter();
          const col = await getRawCollection<WorkshopAttendance>(WorkshopAttendance._collectionName);
          const res = await col
            .aggregate([
              { $match: { _id: { $in: attendances.map(x => x._id) } } },

              ...(filter.bundesland
                ? [
                    {
                      $lookup: {
                        from: Partner._collectionName,
                        localField: 'partnerID',
                        foreignField: '_id',
                        as: 'partners',
                        pipeline: [{ $project: { bundesland: 1 } }],
                      },
                    },

                    { $unwind: '$partners' },

                    { $match: { 'partners.bundesland': filter.bundesland } },
                  ]
                : []),

              // Find the latest training attendance for each user
              {
                $lookup: {
                  from: WorkshopAttendance._collectionName,
                  localField: 'workshopID',
                  foreignField: 'workshopID',
                  as: 'training',
                  let: {
                    parentUserID: '$userID',
                  },
                  pipeline: [
                    {
                      $match: {
                        _class: WorkshopTrainingAttendance._class,
                        state: WorkshopAttendanceStatus.ATTENDED,
                        $expr: {
                          $eq: ['$userID', '$$parentUserID'],
                        },
                      },
                    },
                    {
                      $sort: {
                        date: -1,
                      },
                    },
                    {
                      $limit: 1,
                    },
                  ],
                },
              },

              // Unwind the training array
              {
                $unwind: {
                  path: '$training',
                  preserveNullAndEmptyArrays: true,
                },
              },

              // Find first workshop attendance for each user
              {
                $lookup: {
                  from: WorkshopAttendance._collectionName,
                  localField: 'workshopID',
                  foreignField: 'workshopID',
                  as: 'firstWorkshop',
                  let: {
                    parentUserID: '$userID',
                  },
                  pipeline: [
                    {
                      $match: {
                        _class: WorkshopAttendance._class,
                        $expr: {
                          $eq: ['$userID', '$$parentUserID'],
                        },
                      },
                    },
                    {
                      $sort: {
                        date: -1,
                      },
                    },
                    {
                      $limit: 1,
                    },
                    {
                      $project: {
                        date: 1,
                      },
                    },
                  ],
                },
              },

              // Unwind the training array
              {
                $unwind: {
                  path: '$firstWorkshop',
                  preserveNullAndEmptyArrays: true,
                },
              },

              // Create date from js dates: date - training.date only if training.date exists
              {
                $set: {
                  daysSinceTraining: {
                    $cond: {
                      if: { $eq: ['$training', null] },
                      then: null,
                      else: {
                        $ceil: {
                          $divide: [
                            {
                              $abs: {
                                $subtract: [
                                  {
                                    $toLong: '$firstWorkshop.date',
                                  },
                                  {
                                    $toLong: '$training.date',
                                  },
                                ],
                              },
                            },
                            1000 * 60 * 60 * 24,
                          ],
                        },
                      },
                    },
                  },
                },
              },

              // Final filter
              ...(Object.keys(aggregationFilter).length > 0 ? [{ $match: aggregationFilter }] : []),
            ])
            .toArray();

          return res.map(
            r =>
              new WorkshopAttendanceSearchResult({
                ...r,
                _class: WorkshopAttendanceSearchResult._class,
              }),
          );
        }),
        catchError(e => {
          return of([] as WorkshopAttendanceSearchResult[]);
        }),
      );
    };
  },
  params: [
    {
      name: 'skip',
      type: () => Int,
    },
    {
      name: 'limit',
      type: () => Int,
    },
    {
      name: 'filter',
      type: () => WorkshopFilter,
    },
  ],
});
